import { Alert, Button, Drawer, Form, Input, InputNumber, Select } from 'antd';
import { useEffect } from 'react';
import type { RoleWithPermissions } from '../../roles/roles.api';
import type { RuleEditor } from '../hooks/use-alert-rules';
import { ALERT_SEVERITY_LABELS, ALERT_TYPE_LABELS, toOptions } from '../policies/alert-labels';
import { NEW_RULE_VALUES, toFormValues, type AlertRuleFormValues } from '../policies/alert-rule-form';
import { AlertRuleScopeFields } from './AlertRuleScopeFields';
import { AlertRuleThresholdFields } from './AlertRuleThresholdFields';

interface AlertRuleDrawerProps {
  editor: RuleEditor;
  roles: RoleWithPermissions[];
  error: string | null;
  saving: boolean;
  onSave: (values: AlertRuleFormValues) => Promise<boolean>;
  onClose: () => void;
}

/** Create or edit a rule. Editing changes the rule in place; alerts already raised keep the name and wording they were raised with. */
export function AlertRuleDrawer({ editor, roles, error, saving, onSave, onClose }: AlertRuleDrawerProps) {
  const [form] = Form.useForm<AlertRuleFormValues>();

  useEffect(() => {
    if (editor !== null) form.setFieldsValue(editor === 'new' ? NEW_RULE_VALUES : toFormValues(editor));
  }, [editor, form]);

  return (
    <Drawer open={editor !== null} onClose={onClose} title={editor === 'new' ? 'Create alert rule' : 'Edit alert rule'} size={460} destroyOnHidden forceRender>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<AlertRuleFormValues> form={form} layout="vertical" requiredMark={false} onFinish={onSave} disabled={saving} initialValues={NEW_RULE_VALUES}>
        <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Enter a name' }]}>
          <Input maxLength={120} />
        </Form.Item>
        <Form.Item name="type" label="Type">
          <Select options={toOptions(ALERT_TYPE_LABELS)} />
        </Form.Item>
        <AlertRuleThresholdFields form={form} />
        <AlertRuleScopeFields form={form} />
        <Form.Item name="severity" label="Severity">
          <Select options={toOptions(ALERT_SEVERITY_LABELS)} />
        </Form.Item>
        <Form.Item name="notifyRoleKeys" label="Tell these roles">
          <Select mode="multiple" placeholder="Nobody" options={roles.map((role) => ({ value: role.key, label: role.name }))} />
        </Form.Item>
        <Form.Item
          name="cooldownMinutes"
          label="Quiet time after resolving (minutes)"
          extra="The same rule stays quiet on that truck for this long once an alert is resolved."
          rules={[{ required: true, type: 'number', min: 1, message: 'Enter at least 1 minute' }]}
        >
          <InputNumber min={1} className="w-32" />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={saving}>
            {editor === 'new' ? 'Create rule' : 'Save rule'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
